/**
 * Enrutado, temas y control de acceso por permisos.
 *
 * Una sola aplicación con dos disposiciones (pantalla de salidas y back-office),
 * cada una en tema claro u oscuro. Tras identificarse, cada persona ve solo lo
 * que su rol y permisos autorizan; las rutas lo comprueban para que tampoco valga
 * escribir la URL a mano.
 */

import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { Suspense, lazy, useMemo } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import type { Permiso } from './api/cliente'
import { Acceso } from './componentes/Acceso'
import { Cargando } from './componentes/comunes'
import { Version } from './componentes/Version'
import { PantallaSalidas } from './salidas/PantallaSalidas'
import { crearTemaAdmin, crearTemaSalidas } from './tema'
import { useSesion } from './contexto/Sesion'
import { useTema } from './contexto/Tema'

// El back-office se carga en diferido. Su tabla de datos pesa mas que todo el
// resto de la aplicacion junta, y salidas funciona en tableta: no debe descargar
// codigo que no usa cuando lo que importa es que la pantalla este lista al
// llegar el camion.
const Carga = lazy(() => import('./admin/Carga').then((m) => ({ default: m.Carga })))
const DetalleDocumento = lazy(() =>
  import('./admin/DetalleDocumento').then((m) => ({ default: m.DetalleDocumento })),
)
const Disposicion = lazy(() =>
  import('./admin/Disposicion').then((m) => ({ default: m.Disposicion })),
)
const Documentos = lazy(() =>
  import('./admin/Documentos').then((m) => ({ default: m.Documentos })),
)
const Direcciones = lazy(() => import('./admin/Direcciones').then((m) => ({ default: m.Direcciones })))
const Empresas = lazy(() => import('./admin/Empresas').then((m) => ({ default: m.Empresas })))
const Transportistas = lazy(() =>
  import('./admin/Transportistas').then((m) => ({ default: m.Transportistas })),
)
const Configuracion = lazy(() =>
  import('./admin/Configuracion').then((m) => ({ default: m.Configuracion })),
)
const Manual = lazy(() => import('./componentes/Manual').then((m) => ({ default: m.Manual })))
const Usuarios = lazy(() => import('./admin/Usuarios').then((m) => ({ default: m.Usuarios })))

/**
 * Exige sesión y, opcionalmente, permiso. `permisos` se satisface con tener al
 * menos uno de ellos; `soloAdmin` exige el rol de administrador.
 */
function Protegida({
  children,
  permisos,
  soloAdmin,
}: {
  children: React.ReactNode
  permisos?: Permiso[]
  soloAdmin?: boolean
}) {
  const { credenciales, perfil, puede } = useSesion()
  if (!credenciales || !perfil) {
    return <Navigate to="/acceso" replace />
  }
  if (soloAdmin && !perfil.es_administrador) {
    return <Navigate to="/admin" replace />
  }
  if (permisos && !permisos.some(puede)) {
    return <Navigate to="/acceso" replace />
  }
  return <>{children}</>
}

/** Lleva la raíz del back-office a la primera sección que el usuario puede ver. */
function InicioAdmin() {
  const { puede, perfil } = useSesion()
  if (puede('documentos')) return <Navigate to="/admin/documentos" replace />
  if (puede('transportistas')) return <Navigate to="/admin/transportistas" replace />
  if (puede('direcciones')) return <Navigate to="/admin/direcciones" replace />
  if (puede('empresas')) return <Navigate to="/admin/empresas" replace />
  if (perfil?.es_administrador) return <Navigate to="/admin/usuarios" replace />
  if (puede('salidas')) return <Navigate to="/salidas" replace />
  return <Navigate to="/acceso" replace />
}

// Quien solo tiene 'salidas' entra en el back-office únicamente para el manual:
// su inicio (/admin) le devuelve a la pantalla de salidas.
const PERMISOS_ADMIN: Permiso[] = ['documentos', 'transportistas', 'direcciones', 'empresas', 'salidas']

function conSuspense(elemento: React.ReactNode) {
  return <Suspense fallback={<Cargando />}>{elemento}</Suspense>
}

export function App() {
  const { modo } = useTema()
  const temaAdmin = useMemo(() => crearTemaAdmin(modo), [modo])
  const temaSalidas = useMemo(() => crearTemaSalidas(modo), [modo])

  return (
    <Routes>
      <Route
        path="/acceso"
        element={
          <ThemeProvider theme={temaAdmin}>
            <CssBaseline />
            <Version />
            <Acceso />
          </ThemeProvider>
        }
      />

      <Route
        path="/salidas"
        element={
          <ThemeProvider theme={temaSalidas}>
            <CssBaseline />
            <Version />
            <Protegida permisos={['salidas']}>
              <PantallaSalidas />
            </Protegida>
          </ThemeProvider>
        }
      />

      <Route
        path="/admin"
        element={
          <ThemeProvider theme={temaAdmin}>
            <CssBaseline />
            <Version />
            <Protegida permisos={PERMISOS_ADMIN}>
              {conSuspense(<Disposicion />)}
            </Protegida>
          </ThemeProvider>
        }
      >
        <Route index element={<InicioAdmin />} />
        <Route
          path="documentos"
          element={<Protegida permisos={['documentos']}>{conSuspense(<Documentos />)}</Protegida>}
        />
        <Route
          path="documentos/:uuid"
          element={<Protegida permisos={['documentos']}>{conSuspense(<DetalleDocumento />)}</Protegida>}
        />
        <Route
          path="carga"
          element={<Protegida permisos={['documentos']}>{conSuspense(<Carga />)}</Protegida>}
        />
        <Route
          path="transportistas"
          element={<Protegida permisos={['transportistas']}>{conSuspense(<Transportistas />)}</Protegida>}
        />
        <Route
          path="direcciones"
          element={<Protegida permisos={['direcciones']}>{conSuspense(<Direcciones />)}</Protegida>}
        />
        <Route
          path="empresas"
          element={<Protegida permisos={['empresas']}>{conSuspense(<Empresas />)}</Protegida>}
        />
        <Route path="usuarios" element={<Protegida soloAdmin>{conSuspense(<Usuarios />)}</Protegida>} />
        <Route
          path="configuracion"
          element={<Protegida soloAdmin>{conSuspense(<Configuracion />)}</Protegida>}
        />
        <Route path="manual" element={conSuspense(<Manual />)} />
      </Route>

      <Route path="*" element={<Navigate to="/acceso" replace />} />
    </Routes>
  )
}
